import React, { useState } from 'react';
import styled from 'styled-components';
import YearSelect from 'components/FormFields/YearSelect';
import CodeBlock from '../../components/CodeBlock';
import ComponentDemo from '../../components/ComponentDemo';
import PropsTable from '../../components/PropsTable';

const PageTitle = styled.h1`
  font-size: 36px;
  font-weight: 700;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 16px 0;
`;

const PageDescription = styled.p`
  font-size: 18px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  margin: 0 0 48px 0;
  line-height: 1.6;
`;

const Section = styled.section`
  margin-bottom: 64px;
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: ${(props) => (props.theme.name === 'dark' ? '#F3F4F6' : '#1F2937')};
  margin: 0 0 24px 0;
`;

const DemoRow = styled.div`
  display: flex;
  gap: 24px;
  flex-wrap: wrap;

  > div {
    flex: 1;
    min-width: 200px;
  }
`;

const SelectedValue = styled.div`
  margin-top: 16px;
  padding: 12px;
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#F9FAFB'};
  border-radius: 8px;
  font-size: 13px;
  color: ${(props) => (props.theme.name === 'dark' ? '#9CA3AF' : '#6B7280')};

  code {
    color: ${(props) => (props.theme.name === 'dark' ? '#F3F4F6' : '#1F2937')};
  }
`;

const YearSelectDocs = () => {
  const [yearValue, setYearValue] = useState(2026);
  const [labeledYearValue, setLabeledYearValue] = useState(2026);

  const props = [
    {
      name: 'value',
      type: 'number',
      required: false,
      description: 'The selected year value (defaults to current year)',
    },
    {
      name: 'onChange',
      type: 'function',
      required: true,
      description: 'Callback when year changes: (event) => void',
    },
    {
      name: 'width',
      type: 'string',
      required: false,
      description: 'Width of the select (default: "150px")',
    },
    {
      name: 'label',
      type: 'string',
      required: false,
      description: 'Optional label for the select',
    },
  ];

  return (
    <>
      <PageTitle>YearSelect</PageTitle>
      <PageDescription>
        A specialized year dropdown component with consistent styling across all
        pages. Displays 4 year options: current year -2, current year -1,
        current year, and current year +1 in ascending order.
      </PageDescription>

      <Section>
        <SectionTitle>Props</SectionTitle>
        <PropsTable props={props} />
      </Section>

      <Section>
        <SectionTitle>Basic Usage</SectionTitle>
        <ComponentDemo
          title="Standard year selector"
          code={`import YearSelect from 'components/FormFields/YearSelect';

const [yearFilter, setYearFilter] = useState(2026);

<YearSelect
  value={yearFilter}
  onChange={(e) => setYearFilter(Number(e.target.value))}
/>`}
        >
          <DemoRow>
            <div>
              <YearSelect
                value={yearValue}
                onChange={(e) => setYearValue(Number(e.target.value))}
              />
              <SelectedValue>
                Selected year: <code>{yearValue}</code>
              </SelectedValue>
            </div>
          </DemoRow>
        </ComponentDemo>
      </Section>

      <Section>
        <SectionTitle>With Label</SectionTitle>
        <ComponentDemo
          title="Year selector with label"
          code={`<YearSelect
  label="Filter by Year"
  value={yearFilter}
  onChange={(e) => setYearFilter(Number(e.target.value))}
/>`}
        >
          <DemoRow>
            <div>
              <YearSelect
                label="Filter by Year"
                value={labeledYearValue}
                onChange={(e) => setLabeledYearValue(Number(e.target.value))}
              />
            </div>
          </DemoRow>
        </ComponentDemo>
      </Section>

      <Section>
        <SectionTitle>Common Usage Pattern</SectionTitle>
        <ComponentDemo
          title="In a toolbar with search"
          code={`import YearSelect from 'components/FormFields/YearSelect';
import SearchInput from 'components/SearchInput';
import PageToolbar from 'components/PageToolbar';

<PageToolbar>
  <SearchInput
    onSearch={handleSearch}
    placeholder="Search..."
  />
  <YearSelect
    value={yearFilter}
    onChange={(e) => {
      setYearFilter(Number(e.target.value));
      updateQuery({ year: e.target.value, page: 1 });
    }}
  />
</PageToolbar>`}
        >
          <DemoRow>
            <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
              <input
                type="text"
                placeholder="Search..."
                style={{
                  padding: '10px 14px',
                  borderRadius: '8px',
                  border: '1px solid #ccc',
                  flex: 1,
                }}
              />
              <YearSelect
                value={yearValue}
                onChange={(e) => setYearValue(Number(e.target.value))}
              />
            </div>
          </DemoRow>
        </ComponentDemo>
      </Section>

      <Section>
        <SectionTitle>Import</SectionTitle>
        <CodeBlock
          language="javascript"
          code={`import YearSelect from 'components/FormFields/YearSelect';`}
        />
      </Section>

      <Section>
        <SectionTitle>Year Options</SectionTitle>
        <p style={{ lineHeight: 1.8, marginBottom: '16px' }}>
          The component automatically generates 4 year options based on the
          current year:
        </p>
        <ul style={{ lineHeight: 1.8 }}>
          <li>Current year - 2 (e.g., 2024)</li>
          <li>Current year - 1 (e.g., 2025)</li>
          <li>Current year (e.g., 2026)</li>
          <li>Current year + 1 (e.g., 2027)</li>
        </ul>
      </Section>

      <Section>
        <SectionTitle>When to Use</SectionTitle>
        <ul style={{ lineHeight: 1.8 }}>
          <li>
            <strong>YearSelect</strong> - Use for filtering data by year
            (credits, events, transactions, etc.)
          </li>
          <li>
            <strong>Select</strong> - Use when you need custom year ranges or
            different options
          </li>
        </ul>
      </Section>

      <Section>
        <SectionTitle>Features</SectionTitle>
        <ul style={{ lineHeight: 1.8 }}>
          <li>Consistent year options across all pages</li>
          <li>4 years: current -2, -1, current, +1</li>
          <li>Ascending order (oldest to newest)</li>
          <li>Default width of 150px for consistent UI</li>
          <li>Built on top of the Select component</li>
          <li>Fully theme-aware (light/dark mode support)</li>
        </ul>
      </Section>
    </>
  );
};

export default YearSelectDocs;
